<!-- Payment Processing Overlay -->
<div *ngIf="isProcessingPayment" class="fixed inset-0 bg-white/90 flex items-center justify-center z-[9999]">
    <div class="flex flex-col items-center gap-4">
        <div class="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
        <h2 class="text-xl font-semibold text-gray-700">Processing your payment...</h2>
        <p class="text-gray-500">Please wait while we update your subscription.</p>
    </div>
</div>

<!-- Welcome Animation Overlay -->
<div *ngIf="showWelcomeAnimation" class="welcome-animation-overlay">
    <div class="welcome-animation-container">
        <app-lottie-animation [animationData]="welcomeAnimationData" [loop]="false" [autoplay]="true" width="25rem"
            height="25rem" containerClass="welcome-lottie">
        </app-lottie-animation>
    </div>
</div>

<!-- Celebration Animation Overlay -->
<div *ngIf="showCelebrationAnimation" class="celebration-animation-overlay">
    <div class="celebration-animation-container">
        <app-lottie-animation [animationData]="celebrationAnimationData" [loop]="false" [autoplay]="true" width="100vw"
            height="100vh" containerClass="celebration-lottie">
        </app-lottie-animation>
    </div>
</div>

<div class="flex flex-col gap-4 p-3 h-full overflow-y-auto scrollbar-thin">
    <div class="flex flex-col gap-2">
        <h4 class="font-bold text-md">How would you like to start?</h4>
        <div class="flex flex-col gap-4">
            <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">

                <div (click)="openAIAssistant()"
                    class="group relative common-card-svg-wrapper cursor-pointer shadow-sm hover:shadow-lg rounded-2xl transition-shadow duration-1000"
                    [class.locked-card]="!documentsAllowed()">
                    <div [innerHTML]="generateWithAISvg"></div>
                    <div *ngIf="documentsAllowed()"
                        class="absolute top-2 right-2 z-30 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        (click)="$event.stopPropagation()">

                        <app-button variant="outlined" severity="contrast" size="sm" icon="pi pi-plus"
                            class="!rounded-lg !w-8 !h-8 !min-w-8 !min-h-8 shadow-md hover:shadow-lg bg-white border-gray-200"
                            (clicked)="openAIAssistant()" aria-label="Open AI assistant">
                        </app-button>

                        <app-button variant="outlined" severity="contrast" size="sm" icon="pi pi-info-circle"
                            class="!rounded-lg !w-8 !h-8 !min-w-8 !min-h-8 shadow-md hover:shadow-lg bg-white border-gray-200"
                            pTooltip="Generate construction documents instantly using project data, standard templates, and organizational knowledge, with built-in collaboration across the document lifecycle"
                            toolTipPosition="bottom" aria-label="Info">
                        </app-button>

                    </div>
                    <div *ngIf="!documentsAllowed()"
                        class="absolute inset-0 bg-white/80 z-30 flex items-center justify-center h-full w-full">
                        <span class="text-sm rounded-base px-2 py-1 theme-text-border">
                            <app-icon [type]="'pi pi-lock'" [size]="'sm'" [source]="'primeng'" pTooltip="Locked!"
                                tooltipPosition="top"></app-icon>
                        </span>
                    </div>
                </div>

                <div class="group relative common-card-svg-wrapper cursor-pointer shadow-sm hover:shadow-lg rounded-2xl transition-shadow duration-1000"
                    (click)="openGenerateContract()" [class.locked-card]="!documentsAllowed()">
                    <div [innerHTML]="contractGeneratorSvg"></div>
                    <img [src]="'images/explore/contract-gen-before.svg' | assetUrl"
                        class="absolute inset-0 w-full h-full top-0 right-0 pointer-events-none opacity-100 group-hover:opacity-0 transition-opacity duration-1000" />
                    <img [src]="'images/explore/contract-gen-after.svg' | assetUrl"
                        class="absolute inset-0 w-full h-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
                    <div *ngIf="documentsAllowed()"
                        class="absolute top-2 right-2 z-30 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        (click)="$event.stopPropagation()">

                        <app-button variant="outlined" severity="contrast" size="sm" icon="pi pi-plus"
                            class="!rounded-lg !w-8 !h-8 !min-w-8 !min-h-8 shadow-md hover:shadow-lg bg-white border-gray-200"
                            (clicked)="openGenerateContract()" aria-label="Open AI assistant">
                        </app-button>

                        <app-button variant="outlined" severity="contrast" size="sm" icon="pi pi-external-link"
                            class="!rounded-lg !w-8 !h-8 !min-w-8 !min-h-8 shadow-md hover:shadow-lg bg-white border-gray-200"
                            (clicked)="navigateToGenerateWithAI('CONTRACT')" aria-label="Go to Generate with AI">
                        </app-button>

                        <app-button variant="outlined" severity="contrast" size="sm" icon="pi pi-info-circle"
                            class="!rounded-lg !w-8 !h-8 !min-w-8 !min-h-8 shadow-md hover:shadow-lg bg-white border-gray-200"
                            pTooltip="Create contracts instantly using templates, clauses, project and organizational knowledge, all managed with built-in collaboration across the contract lifecycle"
                            toolTipPosition="top" aria-label="Info">
                        </app-button>

                    </div>

                    <div *ngIf="!documentsAllowed()"
                        class="absolute inset-0 bg-white/80 z-30 flex items-center justify-center h-full w-full">

                        <span class="text-sm rounded-base px-2 py-1 theme-text-border">
                            <app-icon [type]="'pi pi-lock'" [size]="'sm'" [source]="'primeng'" pTooltip="Locked!"
                                tooltipPosition="top">
                            </app-icon>
                        </span>

                    </div>

                </div>

                <div class="group relative common-card-svg-wrapper cursor-pointer shadow-sm hover:shadow-lg rounded-2xl transition-shadow duration-1000"
                    (click)="navigateToGeotechnicalAnalysis()" [class.locked-card]="!geoTechnicalAnalysisAllowed()">
                    <div [innerHTML]="geotechnicalAnalysisSvg"></div>
                    <img [src]="'images/explore/geo-before.svg' | assetUrl"
                        class="absolute inset-0 w-full h-full top-0 right-0 pointer-events-none opacity-100 group-hover:opacity-0 transition-opacity duration-1000" />
                    <img [src]="'images/explore/geo-after.svg' | assetUrl"
                        class="absolute inset-0 w-full h-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
                    <div *ngIf="geoTechnicalAnalysisAllowed()"
                        class="absolute top-2 right-2 z-30 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        (click)="$event.stopPropagation()">

                        <app-button variant="outlined" severity="contrast" size="sm" icon="pi pi-external-link"
                            class="!rounded-lg !w-8 !h-8 !min-w-8 !min-h-8 shadow-md hover:shadow-lg bg-white border-gray-200"
                            (clicked)="navigateToGeotechnicalAnalysis()" aria-label="Go to Geotechnical Analysis">
                        </app-button>

                        <app-button variant="outlined" severity="contrast" size="sm" icon="pi pi-info-circle"
                            class="!rounded-lg !w-8 !h-8 !min-w-8 !min-h-8 shadow-md hover:shadow-lg bg-white border-gray-200"
                            pTooltip="Analyse geotechnical data to generate insights, formula calculations, vendor analysis, and value engineering reports"
                            toolTipPosition="top" aria-label="Info">
                        </app-button>

                    </div>
                    <div *ngIf="!geoTechnicalAnalysisAllowed()"
                        class="absolute inset-0 bg-white/80 z-30 flex items-center justify-center h-full w-full">
                        <span class="text-sm rounded-base px-2 py-1 theme-text-border">
                            <app-icon [type]="'pi pi-lock'" [size]="'sm'" [source]="'primeng'" pTooltip="Locked!"
                                tooltipPosition="top"></app-icon>
                        </span>
                    </div>
                </div>
                <!-- <div class="group relative common-card-svg-wrapper cursor-pointer" (click)="openAIAssistant()">
                <div [innerHTML]="rfpGeneratorSvg"></div>
                <div *ngIf="documentsAllowed()"
                    class="absolute top-2 right-2 z-30 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    (click)="$event.stopPropagation()">

                    <app-button variant="outlined" severity="contrast" size="sm" icon="pi pi-plus"
                        class="!rounded-lg !w-8 !h-8 !min-w-8 !min-h-8 shadow-md hover:shadow-lg bg-white border-gray-200"
                        (clicked)="openGenerateContract()" aria-label="Open AI assistant">
                    </app-button>

                    <app-button variant="outlined" severity="contrast" size="sm" icon="pi pi-external-link"
                        class="!rounded-lg !w-8 !h-8 !min-w-8 !min-h-8 shadow-md hover:shadow-lg bg-white border-gray-200"
                        (clicked)="navigateToGenerateWithAI('CONTRACT')" aria-label="Go to Generate with AI">
                    </app-button>

                    <app-button variant="outlined" severity="contrast" size="sm" icon="pi pi-info-circle"
                        class="!rounded-lg !w-8 !h-8 !min-w-8 !min-h-8 shadow-md hover:shadow-lg bg-white border-gray-200"
                        toolTip="Generate your documents seamlessly, saving time and ensuring accuracy."
                        toolTipPosition="top" aria-label="Info">
                    </app-button>

                </div>
                <div *ngIf="!documentsAllowed()"
                    class="absolute inset-0 bg-white/80 z-30 flex items-center justify-center h-full w-full">
                    <span class="text-sm rounded-base px-2 py-1 theme-text-border">
                        <app-icon [type]="'pi pi-lock'" [size]="'sm'" [source]="'primeng'" pTooltip="Locked!"
                            tooltipPosition="top"></app-icon>
                    </span>
                </div>
            </div> -->

                <div class="group relative common-card-svg-wrapper cursor-pointer shadow-sm hover:shadow-lg rounded-2xl transition-shadow duration-1000"
                    (click)="openAskBob()" [class.locked-card]="!generalAgentAllowed()">
                    <div [innerHTML]="chatWithBobSvg"></div>
                    <img [src]="'images/explore/bob-before.svg' | assetUrl"
                        class="absolute inset-0 w-full h-full top-0 right-0 pointer-events-none opacity-100 group-hover:opacity-0 transition-opacity duration-1000" />
                    <img [src]="'images/explore/bob-after.svg' | assetUrl"
                        class="absolute inset-0 w-full h-full pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
                    <div *ngIf="generalAgentAllowed()"
                        class="absolute top-2 right-2 z-30 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        (click)="$event.stopPropagation()">

                        <app-button variant="outlined" severity="contrast" size="sm" icon="pi pi-external-link"
                            class="!rounded-lg !w-8 !h-8 !min-w-8 !min-h-8 shadow-md hover:shadow-lg bg-white border-gray-200"
                            (clicked)="openAskBob()">
                        </app-button>

                        <app-button variant="outlined" severity="contrast" size="sm" icon="pi pi-info-circle"
                            class="!rounded-lg !w-8 !h-8 !min-w-8 !min-h-8 shadow-md hover:shadow-lg bg-white border-gray-200"
                            pTooltip="An enterprise AI that leverages comprehensive project and organizational knowledge to deliver instant answers, actionable insights, and execution assistance"
                            toolTipPosition="top" aria-label="Info">
                        </app-button>

                    </div>
                    <div *ngIf="!generalAgentAllowed()"
                        class="absolute inset-0 bg-white/80 z-30 flex items-center justify-center h-full w-full">
                        <span class="text-sm rounded-base px-2 py-1 theme-text-border">
                            <app-icon [type]="'pi pi-lock'" [size]="'sm'" [source]="'primeng'" pTooltip="Locked!"
                                tooltipPosition="top"></app-icon>
                        </span>
                    </div>
                </div>

                <!-- <div class="group relative common-card-svg-wrapper cursor-pointer"
                (click)="openProposalGenerator()" [ngClass]="{'cursor-pointer': documentsAllowed()}">
                <div [innerHTML]="proposalGeneratorSvg"></div>
                <div *ngIf="documentsAllowed()"
                    class="absolute top-2 right-2 z-30 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    (click)="$event.stopPropagation()">

                    <app-button variant="outlined" severity="contrast" size="sm" icon="pi pi-plus"
                        class="!rounded-lg !w-8 !h-8 !min-w-8 !min-h-8 shadow-md hover:shadow-lg bg-white border-gray-200"
                        (clicked)="openGenerateContract()" aria-label="Open AI assistant">
                    </app-button>

                    <app-button variant="outlined" severity="contrast" size="sm" icon="pi pi-external-link"
                        class="!rounded-lg !w-8 !h-8 !min-w-8 !min-h-8 shadow-md hover:shadow-lg bg-white border-gray-200"
                        (clicked)="navigateToGenerateWithAI('CONTRACT')" aria-label="Go to Generate with AI">
                    </app-button>

                    <app-button variant="outlined" severity="contrast" size="sm" icon="pi pi-info-circle"
                        class="!rounded-lg !w-8 !h-8 !min-w-8 !min-h-8 shadow-md hover:shadow-lg bg-white border-gray-200"
                        toolTip="Generate your documents seamlessly, saving time and ensuring accuracy."
                        toolTipPosition="top" aria-label="Info">
                    </app-button>

                </div>
                <div *ngIf="!documentsAllowed()"
                    class="absolute inset-0 bg-white/80 z-30 flex items-center justify-center h-full w-full">
                    <span class="text-sm rounded-base px-2 py-1 theme-text-border">
                        <app-icon [type]="'pi pi-lock'" [size]="'sm'" [source]="'primeng'" pTooltip="Locked!"
                            tooltipPosition="top"></app-icon>
                    </span>
                </div>
            </div> -->
            </div>
            <!-- Workspace Overview Dashboard -->
            <app-workspace-overview></app-workspace-overview>
        </div>
    </div>

    <app-ai-assistant [(visible)]="aiAssistant.open" (onGenerate)="onGenerateWithAi($event)"></app-ai-assistant>

    <app-generate-document [(visible)]="generateContract.open" [docTypeId]="getDocTypeId('CONTRACT')"
        (onGenerate)="onGenerateDocument($event)"></app-generate-document>

    <app-dialog [visible]="rename.open" title="Rename Document" [fullscreenOnMobile]="true" class="max-w-[30rem]"
        (closed)="resetRename()">
        <ng-template #bodyTemplate>
            <div class="flex flex-col px-6 py-4 gap-3">
                <p class="mb-0">Enter the new name for the Document:</p>
                <input class="input" pInputText id="newContractName" (click)="$event.stopPropagation()"
                    [(ngModel)]="rename.newName" autocomplete="off" />
            </div>
        </ng-template>
        <ng-template #footerTemplate>
            <div class="flex justify-end items-center gap-2">
                <app-button label="Cancel" (clicked)="rename.open = false" variant="outlined" severity="contrast" />
                <app-button label="Save" icon="pi pi-save" (clicked)="renameContract($event)" [enableThrottle]="true"
                    variant="filled" severity="primary" />
            </div>
        </ng-template>
    </app-dialog>

    <app-dialog [visible]="dueDate.open" title="Due Date" [fullscreenOnMobile]="true" class="max-w-[30rem]"
        (closed)="resetDueDate()">
        <ng-template #bodyTemplate>
            <div class="flex flex-col px-6 py-4 gap-3">
                <p class="mb-0">Select the Due Date for this document:</p>
                <app-datepicker [(value)]="dueDate.selectedDate" [config]="{
                    showIcon: true,
                    placeholder: 'Select Due Date',
                    dateFormat: 'dd M yy',
                    appendTo: 'body',
                    minDate: currentDate
                }" />
            </div>
        </ng-template>
        <ng-template #footerTemplate>
            <div class="flex justify-end items-center gap-2">
                <app-button label="Cancel" (clicked)="dueDate.open = false" variant="outlined" severity="contrast" />
                <app-button label="Save" icon="pi pi-save" (clicked)="saveDueDate($event)" [enableThrottle]="true"
                    variant="filled" severity="primary" />
            </div>
        </ng-template>
    </app-dialog>

    @if(deleteDialogVisible) {
    <app-delete-confirmation [(visible)]="deleteDialogVisible" headerText="Are you sure?"
        bodyText="This action will delete the document permanently" confirmButtonText="Delete"
        [requireConfirmation]="true" (onConfirm)="confirmDeleteContract()">
    </app-delete-confirmation>
    }

    <!-- <div class="relative overflow-hidden">
        <div class="coming-soon-overlay absolute right-0 top-0 z-50 flex items-center justify-center cursor-pointer"
            (click)="openComingSoon()">
            <img [src]="'images/coming-soon.svg' | assetUrl" alt="coming soon"
                class="max-w-sm md:max-w-sm lg:max-w-md xl:max-w-[33rem] 2xl:max-w-xl">
        </div>
        <div class="fadeout-top-to-bottom" style="--fade-size: 70rem;">
            <h2 class="font-bold text-lg">Contract analysis</h2>
            <div class="grid grid-cols-4 gap-4 pb-2">
                <div class="card group transition-transform flex-shrink-0 relative overflow-hidden" id="step1"
                    [ngClass]="{'cursor-pointer': isCardActive('generateContract')}">
                    <div class="card-data flex h-full px-4 py-4 relative z-10">
                        <div class="flex flex-col h-full gap-2">
                            <h2 class="font-bold text-lg">Generate contract</h2>
                            <span class="text-sm text-gray-800">Create accurate, construction-ready contracts using your
                                repository.
                            </span>
                        </div>
                    </div>
                    <img class="h-14 w-14 md:h-20 md:w-20 lg:h-28 lg:w-28 2xl:h-32 2xl:w-32 z-20 absolute left-4 top-[7rem] transition-transform duration-300 ease-in-out"
                        [src]="'images/explore/tools.svg' | assetUrl" alt="image">
                    <img class="h-24 w-24 md:h-32 md:w-32 lg:h-32 lg:w-32 z-20 absolute right-[-0.25rem] top-[7rem] transition-transform duration-300 ease-in-out group-hover:rotate-[6deg]"
                        [src]="'images/explore/doc-right.svg' | assetUrl" alt="image">
                    <img class="h-24 w-24 md:h-32 md:w-32 lg:h-32 lg:w-32 absolute right-8 top-[7rem] transition-transform duration-300 ease-in-out group-hover:rotate-[-4deg]"
                        [src]="'images/explore/doc-left.svg' | assetUrl" alt="image">
                    <div *ngIf="!isCardActive('generateContract')"
                        class="absolute inset-0 bg-white/80 z-30 flex items-center justify-center h-full w-full">
                        <span class="text-sm rounded-base px-2 py-1 theme-text-border">
                            <span class="theme-text">Coming soon!</span>
                        </span>
                    </div>
                </div>

                <div class="card group transition-transform flex-shrink-0 relative overflow-hidden" id="step1"
                    [ngClass]="{'cursor-pointer': isCardActive('redlining')}">
                    <div class="card-data flex h-full px-4 py-4 relative z-10">
                        <div class="flex flex-col h-full gap-2">
                            <h2 class="font-bold text-lg">Redlining</h2>
                            <span class="text-sm text-gray-800">Review and track contract changes for smooth
                                collaboration.</span>
                        </div>
                    </div>
                    <img class="h-14 w-14 md:h-20 md:w-20 lg:h-28 lg:w-28 2xl:h-32 2xl:w-32 z-20 absolute left-4 top-[7rem] transition-transform duration-300 ease-in-out"
                        [src]="'images/explore/tools.svg' | assetUrl" alt="image">
                    <img class="h-24 w-24 md:h-32 md:w-32 lg:h-32 lg:w-32 z-20 absolute right-[-0.25rem] top-[7rem] transition-transform duration-300 ease-in-out group-hover:rotate-[6deg]"
                        [src]="'images/explore/highlight.svg' | assetUrl" alt="image">
                    <div *ngIf="!isCardActive('redlining')"
                        class="absolute inset-0 bg-white/80 z-30 flex items-center justify-center h-full w-full">
                        <span class="text-sm rounded-base px-2 py-1 theme-text-border">
                            <span class="theme-text">Coming soon!</span>
                        </span>
                    </div>
                </div>


                <div class="card group transition-transform flex-shrink-0 relative overflow-hidden" id="step1"
                    [ngClass]="{'cursor-pointer': isCardActive('complianceCheck')}">
                    <div class="card-data flex h-full px-4 py-4 relative z-10">
                        <div class="flex flex-col h-full gap-2">
                            <h2 class="font-bold text-lg">Compliance Check</h2>
                            <span class="text-sm text-gray-800">Ensure contracts meet standards and reduce risks.</span>
                        </div>
                    </div>
                    <img class="h-14 w-14 md:h-20 md:w-20 lg:h-28 lg:w-28 2xl:h-32 2xl:w-32 z-20 absolute left-4 top-[7rem] transition-transform duration-300 ease-in-out"
                        [src]="'images/explore/tools.svg' | assetUrl" alt="image">
                    <img class="h-24 w-24 md:h-32 md:w-32 lg:h-32 lg:w-32 z-20 absolute right-[-0.25rem] top-[7rem] transition-transform duration-300 ease-in-out group-hover:rotate-[6deg]"
                        [src]="'images/explore/check.svg' | assetUrl" alt="image">
                    <div *ngIf="!isCardActive('complianceCheck')"
                        class="absolute inset-0 bg-white/80 z-30 flex items-center justify-center h-full w-full">
                        <span class="text-sm rounded-base px-2 py-1 theme-text-border">
                            <span class="theme-text">Coming soon!</span>
                        </span>
                    </div>
                </div>


                <div class="card group transition-transform flex-shrink-0 relative overflow-hidden" id="step1"
                    [ngClass]="{'cursor-pointer': isCardActive('compareContracts')}">
                    <div class="card-data flex h-full px-4 py-4 relative z-10">
                        <div class="flex flex-col h-full gap-2">
                            <h2 class="font-bold text-lg">Compare Contracts</h2>
                            <span class="text-sm text-gray-800">Identify key differences and updates instantly.</span>
                        </div>
                    </div>
                    <img class="h-14 w-14 md:h-20 md:w-20 lg:h-28 lg:w-28 2xl:h-32 2xl:w-32 z-20 absolute left-4 top-[7rem] transition-transform duration-300 ease-in-out"
                        [src]="'images/explore/tools.svg' | assetUrl" alt="image">
                    <img class="h-24 w-24 md:h-32 md:w-32 lg:h-32 lg:w-32 z-20 absolute right-[-0.25rem] top-[7rem] transition-transform duration-300 ease-in-out group-hover:rotate-[6deg]"
                        [src]="'images/explore/compare.svg' | assetUrl" alt="image">
                    <div *ngIf="!isCardActive('compareContracts')"
                        class="absolute inset-0 bg-white/80 z-30 flex items-center justify-center h-full w-full">
                        <span class="text-sm rounded-base px-2 py-1 theme-text-border">
                            <span class="theme-text">Coming soon!</span>
                        </span>
                    </div>
                </div>
            </div>

            <h2 class="font-bold text-lg">Proposal analysis</h2>
            <div class="grid grid-cols-4 gap-4">
                <div class="card group transition-transform flex-shrink-0 relative overflow-hidden" id="step1"
                    [ngClass]="{'cursor-pointer': isCardActive('generateProposal')}">
                    <div class="card-data flex h-full px-4 py-4 relative z-10">
                        <div class="flex flex-col h-full gap-2">
                            <h2 class="font-bold text-lg">Generate Proposal</h2>
                            <span class="text-sm text-gray-800">Build professional, consistent proposals using your
                                data.</span>
                        </div>
                    </div>
                    <img class="h-14 w-14 md:h-20 md:w-20 lg:h-28 lg:w-28 2xl:h-32 2xl:w-32 z-20 absolute left-4 top-[7rem] transition-transform duration-300 ease-in-out"
                        [src]="'images/explore/tools.svg' | assetUrl" alt="image">
                    <img class="h-24 w-24 md:h-32 md:w-32 lg:h-32 lg:w-32 z-20 absolute right-[-0.25rem] top-[7rem] transition-transform duration-300 ease-in-out group-hover:rotate-[6deg]"
                        [src]="'images/explore/doc-right.svg' | assetUrl" alt="image">
                    <img class="h-24 w-24 md:h-32 md:w-32 lg:h-32 lg:w-32 absolute right-8 top-[7rem] transition-transform duration-300 ease-in-out group-hover:rotate-[-4deg]"
                        [src]="'images/explore/doc-left.svg' | assetUrl" alt="image">
                    <div *ngIf="!isCardActive('generateProposal')"
                        class="absolute inset-0 bg-white/80 z-30 flex items-center justify-center h-full w-full">
                        <span class="text-sm rounded-base px-2 py-1 theme-text-border">
                            <span class="theme-text">Coming soon!</span>
                        </span>
                    </div>
                </div>
                <div class="card group transition-transform flex-shrink-0 relative overflow-hidden" id="step1"
                    [ngClass]="{'cursor-pointer': isCardActive('rfpVsProposal')}">
                    <div class="card-data flex h-full px-4 py-4 relative z-10">
                        <div class="flex flex-col h-full gap-2">
                            <h2 class="font-bold text-lg">RFP vs proposal</h2>
                            <span class="text-sm text-gray-800">Match proposals with RFP requirements for
                                alignment.</span>
                        </div>
                    </div>
                    <img class="h-14 w-14 md:h-20 md:w-20 lg:h-28 lg:w-28 2xl:h-32 2xl:w-32 z-20 absolute left-4 top-[7rem] transition-transform duration-300 ease-in-out"
                        [src]="'images/explore/tools.svg' | assetUrl" alt="image">
                    <img class="h-24 w-24 md:h-32 md:w-32 lg:h-32 lg:w-32 z-20 absolute right-[-0.25rem] top-[7rem] transition-transform duration-300 ease-in-out group-hover:rotate-[6deg]"
                        [src]="'images/explore/compare.svg' | assetUrl" alt="image">
                    <div *ngIf="!isCardActive('rfpVsProposal')"
                        class="absolute inset-0 bg-white/80 z-30 flex items-center justify-center h-full w-full">
                        <span class="text-sm rounded-base px-2 py-1 theme-text-border">
                            <span class="theme-text">Coming soon!</span>
                        </span>
                    </div>
                </div>

                <div class="card group transition-transform flex-shrink-0 relative overflow-hidden" id="step1"
                    [ngClass]="{'cursor-pointer': isCardActive('compareProposals')}">
                    <div class="card-data flex h-full px-4 py-4 relative z-10">
                        <div class="flex flex-col h-full gap-2">
                            <h2 class="font-bold text-lg">Compare proposals</h2>
                            <span class="text-sm text-gray-800">Spot critical differences and ensure proposals meet
                                needs.</span>
                        </div>
                    </div>
                    <img class="h-14 w-14 md:h-20 md:w-20 lg:h-28 lg:w-28 2xl:h-32 2xl:w-32 z-20 absolute left-4 top-[7rem] transition-transform duration-300 ease-in-out"
                        [src]="'images/explore/tools.svg' | assetUrl" alt="image">
                    <img class="h-24 w-24 md:h-32 md:w-32 lg:h-32 lg:w-32 z-20 absolute right-[-0.25rem] top-[7rem] transition-transform duration-300 ease-in-out group-hover:rotate-[6deg]"
                        [src]="'images/explore/compare.svg' | assetUrl" alt="image">
                    <div *ngIf="!isCardActive('compareProposals')"
                        class="absolute inset-0 bg-white/80 z-30 flex items-center justify-center h-full w-full">
                        <span class="text-sm rounded-base px-2 py-1 theme-text-border">
                            <span class="theme-text">Coming soon!</span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </div> -->

    <!--  <h2 class="font-bold text-lg">Specification analysis</h2>
    <div class="grid grid-cols-4 gap-4 pb-2">
        <div class="card group transition-transform flex-shrink-0 relative overflow-hidden" id="step1"
            [ngClass]="{'cursor-pointer': isCardActive('specAnalyser')}">
            <div class="card-data flex h-full px-4 py-4 relative z-10">
                <div class="flex flex-col h-full gap-2">
                    <h2 class="font-bold text-lg">Spec Analyser</h2>
                    <span class="text-sm text-gray-800">Analyze and interpret construction specifications easily.
                    </span>
                </div>
            </div>
            <img class="h-14 w-14 md:h-20 md:w-20 lg:h-28 lg:w-28 2xl:h-32 2xl:w-32 z-20 absolute left-4 top-[7rem] transition-transform duration-300 ease-in-out"
                [src]="'images/explore/tools.svg' | assetUrl" alt="image">
            <img class="h-24 w-24 md:h-32 md:w-32 lg:h-32 lg:w-32 z-20 absolute right-[-0.25rem] top-[7rem] transition-transform duration-300 ease-in-out group-hover:rotate-[6deg]"
                [src]="'images/explore/doc-right.svg' | assetUrl" alt="image">
            <div *ngIf="!isCardActive('specAnalyser')"
                class="absolute inset-0 bg-white/80 z-30 flex items-center justify-center h-full w-full">
                <span class="text-sm rounded-base px-2 py-1 theme-text-border">
                    <span class="theme-text">Coming soon!</span>
                </span>
            </div>
        </div>
    </div>

    <h2 class="font-bold text-lg">Opportunity analysis</h2>
    <div class="grid grid-cols-4 gap-4 pb-2">
        <div class="card group transition-transform flex-shrink-0 relative overflow-hidden" id="step1"
            [ngClass]="{'cursor-pointer': isCardActive('opportunityFinder')}">
            <div class="card-data flex h-full px-4 py-4 relative z-10">
                <div class="flex flex-col h-full gap-2">
                    <h2 class="font-bold text-lg">Opportunity Finder</h2>
                    <span class="text-sm text-gray-800">Find project opportunities suited to your company.</span>
                </div>
            </div>
            <img class="h-14 w-14 md:h-20 md:w-20 lg:h-28 lg:w-28 2xl:h-32 2xl:w-32 z-20 absolute left-4 top-[7rem] transition-transform duration-300 ease-in-out"
                [src]="'images/explore/tools.svg' | assetUrl" alt="image">
            <img class="h-24 w-24 md:h-32 md:w-32 lg:h-32 lg:w-32 z-20 absolute right-[-0.25rem] top-[7rem] transition-transform duration-300 ease-in-out group-hover:rotate-[6deg]"
                [src]="'images/explore/doc-right.svg' | assetUrl" alt="image">
            <div *ngIf="!isCardActive('opportunityFinder')"
                class="absolute inset-0 bg-white/80 z-30 flex items-center justify-center h-full w-full">
                <span class="text-sm rounded-base px-2 py-1 theme-text-border">
                    <span class="theme-text">Coming soon!</span>
                </span>
            </div>
        </div>
    </div> -->